import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLabels } from 'wsm-common-data';
import { formatCurrency } from 'ddc-intl';
import SingleItem from './SingleItem';
import './styles.scss';

const MAX_PACKAGES_DISPLAY = 3;

const ListStylePackages = ({
	packages,
	options,
	accessories,
	locale,
	onOpenModal,
	togglePackageDetails,
	prefs,
	isModal,
	widgetInfo
}) => {
	const labels = useLabels();
	const showPricing = useSelector((state) => state.settings.showPricing);

	const sortedPackages = useMemo(
		() =>
			[...(packages || [])].sort(
				(a, b) => (b.price || 0) - (a.price || 0)
			),
		[packages]
	);

	const hasPackages = sortedPackages.length > 0;
	const hasOptions = isModal && options && options.length > 0;

	const displayPackages = isModal
		? sortedPackages
		: sortedPackages.slice(0, MAX_PACKAGES_DISPLAY);

	// Packages take priority: once there are 3+ packages, the options
	// grouping is dropped from the collapsed view (still shown in the modal).
	const hasOptionsList = options.length > 0;
	const optionsHiddenByPackages =
		sortedPackages.length >= MAX_PACKAGES_DISPLAY;
	const showOptionsRow =
		!isModal && hasOptionsList && !optionsHiddenByPackages;
	const hasMorePackages =
		!isModal &&
		(sortedPackages.length > MAX_PACKAGES_DISPLAY ||
			(hasOptionsList && optionsHiddenByPackages));

	const totalOptionsPrice = useMemo(
		() => options.reduce((sum, item) => sum + (item.price || 0), 0),
		[options]
	);
	const totalItemsCount =
		displayPackages.length + (showOptionsRow ? 1 : 0) + accessories.length;

	const toPrice = (price) =>
		price > 0 ? formatCurrency(price, locale) : '0';

	if (
		(!packages || packages.length === 0) &&
		(!options || options.length === 0) &&
		(!accessories || accessories.length === 0)
	) {
		return null;
	}

	const isCollapseEnabled = prefs?.toggleCollapse === 'true';
	const listStylePackagesClassName = [
		'list-style-packages',
		isCollapseEnabled && 'list-style-packages-collapse-enabled',
		isModal && 'list-style-packages-modal'
	]
		.filter(Boolean)
		.join(' ');

	const commonItemProps = {
		onOpenModal,
		totalItemsCount,
		togglePackageDetails,
		isEnableDisclaimer: prefs?.enableDisclaimers === 'true',
		layout: prefs?.packagesOptionsLayout || 'list'
	};

	return (
		<div className={listStylePackagesClassName}>
			{isModal && hasPackages && (
				<h4 className="mb-4 mt-0">{labels.get('INCLUDED_PACKAGES')}</h4>
			)}
			{displayPackages.map((pkg, index) => (
				<SingleItem
					key={pkg.name || index}
					index={index}
					itemName={pkg.name}
					subItems={pkg.subitems || []}
					itemType="package"
					itemPrice={toPrice(pkg.price)}
					isFirstItem={index === 0}
					isModal={isModal}
					widgetInfo={widgetInfo}
					{...commonItemProps}
				/>
			))}
			{showOptionsRow && (
				<SingleItem
					itemType="option"
					itemName={labels.get('MORE_PRE_INSTALLED_ITEMS')}
					subItems={options}
					itemPrice={toPrice(totalOptionsPrice)}
					isFirstItem={displayPackages.length === 0}
					{...commonItemProps}
				/>
			)}
			{accessories.map((accessory, index) => (
				<SingleItem
					key={accessory.name || `accessory-${index}`}
					index={index}
					itemName={accessory.name}
					itemType="accessories"
					itemPrice={toPrice(accessory.price)}
					isFirstItem={
						displayPackages.length === 0 &&
						!showOptionsRow &&
						index === 0
					}
					{...commonItemProps}
				/>
			))}
			{hasMorePackages && (
				<div className="see-all-container">
					<button
						type="button"
						className="btn btn-link px-0"
						onClick={onOpenModal}
					>
						{labels.get('SEE_ALL_PACKAGES_OPTIONS')}
					</button>
				</div>
			)}
			{hasOptions && (
				<div
					className={`list-options-section ${
						hasPackages ? 'mt-6' : ''
					}`}
				>
					<h4 className="mb-4 mt-0">
						{labels.get('INCLUDED_OPTIONS')}
					</h4>
					<ul className="list-unstyled list-style-options">
						{options.map(({ name, price }, idx) => (
							<li
								key={name || idx}
								className="list-style-option-item py-2"
							>
								<span>{name}</span>
								{showPricing && price > 0 && (
									<div className="package-value mt-3">
										{`${formatCurrency(price, locale)} ${(
											labels.get('VALUE') || 'value'
										).toLowerCase()}`}
									</div>
								)}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

ListStylePackages.defaultProps = {
	packages: [],
	options: [],
	accessories: [],
	locale: 'en_US',
	togglePackageDetails: false,
	prefs: {},
	isModal: false,
	widgetInfo: null
};

ListStylePackages.propTypes = {
	packages: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			subitems: PropTypes.arrayOf(PropTypes.shape({})),
			price: PropTypes.number
		})
	),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			price: PropTypes.number
		})
	),
	accessories: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			price: PropTypes.number
		})
	),
	locale: PropTypes.string,
	onOpenModal: PropTypes.func,
	togglePackageDetails: PropTypes.bool,
	prefs: PropTypes.shape({
		toggleCollapse: PropTypes.string,
		enableDisclaimers: PropTypes.string,
		packagesOptionsLayout: PropTypes.string
	}),
	isModal: PropTypes.bool,
	widgetInfo: PropTypes.shape({
		dealerId: PropTypes.string,
		vehicleId: PropTypes.string
	})
};

export default ListStylePackages;
